/**
 * Player-facing number and time formats for the station screens. Game time is in-space flight time (the clock
 * stops while docked), so durations read "of flight".
 */
import { formatCredits, formatTonnes } from '../career/result'

export { formatCredits, formatTonnes }

/** A duration in game seconds: "45 s", "23 min", "1 h 05 min". */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  if (s < 60) return `${s} s`
  const totalMin = Math.floor(s / 60)
  if (totalMin < 60) return `${totalMin} min`
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  return `${h} h ${String(m).padStart(2, '0')} min`
}

/** Time until a game-time moment: "in 23 min of flight", or "due now" once it has passed. */
export function formatDueIn(at: number, now: number): string {
  const left = at - now
  if (left <= 0) return 'due now'
  return `in ${formatDuration(left)} of flight`
}

/** Credits with the unit: "12,400 cr", with a minus sign (not a hyphen) below zero: "−3,200 cr" (D-459 nit). */
export function formatCr(amount: number): string {
  const n = Math.round(amount)
  const digits = formatCredits(Math.abs(n))
  return n < 0 ? `−${digits} cr` : `${digits} cr`
}

/** A signed percentage to whole percent: 0.123 → "+12%", -0.05 → "−5%". */
export function formatSignedPct(fraction: number): string {
  const p = Math.round(fraction * 100)
  if (p === 0) return '0%'
  return p > 0 ? `+${p}%` : `−${-p}%`
}

/** A fraction as whole percent: 0.64 → "64%". */
export function formatPct(fraction: number): string {
  return `${Math.round(fraction * 100)}%`
}

/** A standing (−100..100) with its sign: "+12", "−5", "0". */
export function formatStanding(value: number): string {
  const v = Math.round(value)
  if (v === 0) return '0'
  return v > 0 ? `+${v}` : `−${-v}`
}

/** An ISO time as local "YYYY-MM-DD HH:MM" (empty for a missing or invalid value). */
export function formatSavedAt(iso: string | undefined): string {
  if (iso === undefined) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const two = (n: number): string => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())} ${two(d.getHours())}:${two(d.getMinutes())}`
}
